/**
 * The "Generate site defaults" bundle, planned per language: which empty site-level fields Lumio is asked for,
 * how many pages inherit the site description, and the title template set without Lumio when it is empty (the
 * server never writes a site-level title). Pure — the UI owns the selection and the calls.
 */
import { isFooterHtmlEmpty } from './footer-html';
import type { SiteSeoDocument, SiteSeoGenerateField, SiteSeoGenerateRequest } from './seo-types';
import { TITLE_PAGE_TOKEN } from './title-template';

export interface LanguageDefaultsPlan {
  language: string;
  /** The empty site fields Lumio is asked for in this language; [] = nothing to fill. */
  fields: SiteSeoGenerateField[];
  /** Exact + pattern routes without their own description in this language (only when 'description' is in fields). */
  pagesUsingDefault: number;
  pagesKeepingOwn: number;
  /** The title template set in this language, by the page and free of charge (never by Lumio), because the
   *  language has none; null = a template is written (or there is no brand name to put in one). */
  titleTemplate: string | null;
}

export interface DefaultsPlan {
  languages: LanguageDefaultsPlan[];
  /** Languages with something to fill: a Lumio field or the title template. */
  fillable: number;
}

const isBlank = (value: string | undefined): boolean => (value ?? '').trim() === '';

/** The title template the bundle sets where a language has none: the page, then the brand. */
export const defaultTitleTemplate = (brandName: string): string => `${TITLE_PAGE_TOKEN} | ${brandName.trim()}`;

/** Whether the bundle sets the title template of `language` in `doc`: only an empty one, and only with a brand. */
export const titleTemplateToSet = (doc: SiteSeoDocument, language: string, brandName: string): string | null =>
  isBlank(doc.site.title_template?.[language]) && !isBlank(brandName) ? defaultTitleTemplate(brandName) : null;

/** A language the bundle has something to do for (a Lumio call, the title template, or both). */
export const hasDefaultsWork = (plan: LanguageDefaultsPlan): boolean =>
  plan.fields.length > 0 || plan.titleTemplate !== null;

const planLanguage = (
  doc: SiteSeoDocument,
  language: string,
  isDefault: boolean,
  footerOverridable: boolean,
  brandName: string,
): LanguageDefaultsPlan => {
  const fields: SiteSeoGenerateField[] = [];
  if (isBlank(doc.site.description?.[language])) fields.push('description');
  if (footerOverridable && isFooterHtmlEmpty(doc.site.footer_text?.[language] ?? '')) fields.push('footer_text');
  if (isDefault && isBlank(doc.site.ai.llms_summary)) fields.push('llms_summary');
  if (isDefault && isBlank(doc.site.ai.llms_facts)) fields.push('llms_facts');

  let pagesUsingDefault = 0;
  let pagesKeepingOwn = 0;
  if (fields.includes('description')) {
    for (const route of doc.routes) {
      if (route.match === 'wildcard') continue;
      if (isBlank(route.description?.[language])) pagesUsingDefault += 1;
      else pagesKeepingOwn += 1;
    }
  }
  return {
    language,
    fields,
    pagesUsingDefault,
    pagesKeepingOwn,
    titleTemplate: titleTemplateToSet(doc, language, brandName),
  };
};

export const defaultsPlan = (
  doc: SiteSeoDocument,
  languages: { default: string; all: string[] },
  footerOverridable: boolean,
  brandName: string,
): DefaultsPlan => {
  const planned = languages.all.map((language) =>
    planLanguage(doc, language, language === languages.default, footerOverridable, brandName),
  );
  return { languages: planned, fillable: planned.filter(hasDefaultsWork).length };
};

/** One site-level call (no `path`) per language. */
export const defaultsRequest = (plan: LanguageDefaultsPlan): SiteSeoGenerateRequest => ({
  language: plan.language,
  fields: plan.fields,
  footer_mode: 'text',
});

/** The server charges one `site_seo` price per call, so: cost x selected languages with a Lumio field to fill (the
 *  title template is free). */
export const defaultsTotal = (
  plan: DefaultsPlan,
  selected: ReadonlySet<string>,
  cost: number | undefined,
): number | undefined => {
  if (cost === undefined) return undefined;
  const calls = plan.languages.filter((p) => p.fields.length > 0 && selected.has(p.language)).length;
  return calls * cost;
};
